<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import AllocationsEnable from '../components/allocations/AllocationsEnable.vue';
import ProgressFrame from './layout/ProgressFrame.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import HorizontalContentRow from '../components/HorizontalContentRow.vue';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import { useDeviceSpecificationStore } from '../stores/deviceSpecificationStore';
import type {
  AllocGroupIndex,
  AllocPhaseIndex,
  TerminalAllocation,
} from '../models/SolshareSystemSpec';
import { getKwpAllocationPerPhase } from '../utils/allocations';
import { ref, watch } from 'vue';
import type { FormInstance } from 'ant-design-vue';
import { useRouter } from 'vue-router';
import { KWP_ALLOC_COUNTRIES } from '../constants/config-constants';
import { COUNTRY_CODE } from '@/utils/constants';

const { t } = useI18n();

const commissioningDeviceStore = useCommissioningDeviceStore();
const serialNumber = commissioningDeviceStore.serialNumber;

const deviceSpecificationStore = useDeviceSpecificationStore();

const baseKwp = deviceSpecificationStore.solshareSystemSpec.inverterSpec.systemSize ?? 0;
const kwpPerPhase = getKwpAllocationPerPhase(serialNumber, baseKwp);
const showAsKwp = KWP_ALLOC_COUNTRIES.includes(
  deviceSpecificationStore.solshareSite?.country as COUNTRY_CODE,
);

const formRef = ref<FormInstance>();
const formData = ref<TerminalAllocation>(deviceSpecificationStore.draft.allocations);

const algorithmType = deviceSpecificationStore.draft.algorithmType;

const router = useRouter();

watch(
  formData,
  (newVal: TerminalAllocation) => {
    console.log('formData changed', newVal); //TODO: remove
    deviceSpecificationStore.updateDraftTerminalAllocation(newVal);
  },
  { deep: true },
);

const updateEnableValue = (
  phaseIndex: AllocPhaseIndex,
  groupIndex: AllocGroupIndex,
  value: boolean,
) => {
  const phase = formData.value[phaseIndex];
  if (phase && phase[groupIndex]) {
    phase[groupIndex].enable = value;
  }
};

const confirmAllocations = () => {
  commissioningDeviceStore.isUploadConfig = true;
  router.push({ name: 'StartCommissioning' });
};
</script>

<template>
  <ProgressFrame pageClass="subsection-page" withBackArrow titleTight subTitleColor="secondary">
    <template #title>{{ t('commissioning.configureAllocationsConfirm.pageTitle') }}</template>
    <template #subTitle>{{ serialNumber }}</template>

    <HorizontalContentRow class="horizontal-content instruction-text">
      Select / deselect the tenancy connections to commission.
    </HorizontalContentRow>

    <a-form ref="formRef" :model="formData">
      <template v-for="(_, phaseIndexKey) in formData" :key="`L${phaseIndexKey}`">
        <HorizontalContentRow
          :lastRow="Number(phaseIndexKey) === 2"
          noSidePadding
          :class="['horizontal-content', Number(phaseIndexKey) === 0 ? 'first-row' : '']"
        >
          <AllocationsEnable
            :phaseIndex="Number(phaseIndexKey) as AllocPhaseIndex"
            :phaseAllocations="formData[phaseIndexKey as AllocPhaseIndex]"
            :algorithmType="algorithmType"
            @updateEnableValue="updateEnableValue"
            :showAsKwp="showAsKwp"
            :kwpPerPhase="kwpPerPhase"
          />
        </HorizontalContentRow>
      </template>
    </a-form>

    <div class="end-of-page">
      <ButtonPrimary block @click="confirmAllocations" data-tn="next-btn"> Confirm </ButtonPrimary>
    </div>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.horizontal-content {
  &.instruction-text {
    text-align: center;
    font-weight: @font-weight-medium;
  }

  &.first-row {
    padding-top: 0;
  }
}

.end-of-page {
  margin: @content-side-margin;
}
</style>
